import { useT } from '../i18n/messages';
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  ComponentPropsWithoutRef,
  ElementType,
  HTMLAttributes,
  InputHTMLAttributes,
  PropsWithChildren,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import { Zap } from 'lucide-react';
import { Dropdown } from '@zektec/ui';

const baseThemeStyle = {
  '--zek-font-sans': "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  '--zek-font-brand': "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
} as CSSProperties;

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface SelectOption {
  label: string;
  labelKey?: string;
  value: string;
  disabled?: boolean;
}

function buttonVariantClass(variant: ButtonVariant) {
  switch (variant) {
    case 'danger':
      return 'danger-button';
    case 'ghost':
    case 'secondary':
      return 'ghost-button';
    default:
      return 'primary-button';
  }
}

export function Button({
  className,
  type = 'button',
  variant = 'primary',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
}) {
  return (
    <button
      {...props}
      className={[buttonVariantClass(variant), className].filter(Boolean).join(' ')}
      type={type}
    />
  );
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div {...props} className={className} />;
}

export function ZekTecLogo({
  className,
  compact = false,
  withWordmark = true,
}: {
  className?: string;
  compact?: boolean;
  withWordmark?: boolean;
}) {
  const t = useT();
  return (
    <div
      className={['zektec-logo', compact ? 'zektec-logo--compact' : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <Zap aria-hidden="true" className="zektec-logo__mark" strokeWidth={2.4} />
      {withWordmark ? (
        <span className="zektec-logo__wordmark">
          <strong>ZekTec</strong>
          <span>{t('billing.ui.billing.space')}</span>
        </span>
      ) : null}
    </div>
  );
}

export function TextInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={['input', className].filter(Boolean).join(' ')} />;
}

export function TextArea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={['textarea', className].filter(Boolean).join(' ')} />;
}

export function Field({
  children,
  description,
  error,
  label,
}: {
  children: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  label: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {description ? <span className="field__description">{description}</span> : null}
      {error ? <span className="field__error">{error}</span> : null}
    </label>
  );
}

export function CheckboxField({
  checked,
  description,
  label,
  onChange,
}: {
  checked: boolean;
  description?: ReactNode;
  label: ReactNode;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="checkbox-field">
      <span className="checkbox-field__control">
        <input
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          type="checkbox"
        />
      </span>
      <span className="checkbox-field__content">
        <span className="field__label">{label}</span>
        {description ? <span className="field__description">{description}</span> : null}
      </span>
    </label>
  );
}

export function Select({
  allowClear = true,
  className,
  disabled = false,
  loading = false,
  onChange,
  options,
  placeholder,
  value,
}: {
  allowClear?: boolean;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: string | null) => void;
  options: SelectOption[];
  placeholder?: string;
  value: string | null | undefined;
}) {
  const t = useT();
  const resolvedPlaceholder = placeholder ?? t('billing.ui.select.an.option');
  const translatedOptions = options.map((option) => ({
    ...option,
    label: option.labelKey ? t(option.labelKey) : option.label,
  }));
  const resolvedOptions = allowClear
    ? [{ label: resolvedPlaceholder, value: '' }, ...translatedOptions]
    : translatedOptions;

  return (
    <Dropdown
      className={className}
      disabled={disabled || loading}
      onChange={(nextValue) => onChange(nextValue || null)}
      options={
        resolvedOptions.length === 0
          ? [{ label: t('billing.ui.no.options'), value: '', disabled: true }]
          : loading
            ? [
                {
                  label: t('billing.ui.loading.options'),
                  value: '',
                  disabled: true,
                },
              ]
            : resolvedOptions
      }
      placeholder={loading ? t('billing.ui.loading.options') : resolvedPlaceholder}
      value={value ?? ''}
    />
  );
}

export const darkTheme = baseThemeStyle;

export function ThemeProvider<T extends ElementType = 'div'>({
  as,
  children,
  className,
  theme,
  style,
  ...props
}: PropsWithChildren<
  {
    as?: T;
    className?: string;
    theme?: CSSProperties;
    style?: CSSProperties;
  } & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className' | 'style'>
>) {
  const Component = (as ?? 'div') as ElementType;
  return (
    <Component {...props} className={className} style={{ ...baseThemeStyle, ...theme, ...style }}>
      {children}
    </Component>
  );
}
